/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type LocationState = {
  pathname: string
  search: string
}

type RouterValue = LocationState & {
  navigate: (to: string, replace?: boolean) => void
}

const RouterContext = createContext<RouterValue | null>(null)

function currentLocation(): LocationState {
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  }
}

export function BrowserRouter({ children }: PropsWithChildren) {
  const [location, setLocation] = useState(currentLocation)

  useEffect(() => {
    const onPopState = () => setLocation(currentLocation())
    window.addEventListener('popstate', onPopState)
    return () => window.removeEventListener('popstate', onPopState)
  }, [])

  const navigate = useCallback((to: string, replace = false) => {
    const target = new URL(to, window.location.origin)
    const next = `${target.pathname}${target.search}${target.hash}`
    if (replace) {
      window.history.replaceState(null, '', next)
    } else {
      window.history.pushState(null, '', next)
    }
    setLocation(currentLocation())
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [])

  const value = useMemo(
    () => ({ ...location, navigate }),
    [location, navigate],
  )

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  )
}

function useRouter() {
  const router = useContext(RouterContext)
  if (router === null) throw new Error('Router non inizializzato')
  return router
}

type LinkProps = PropsWithChildren<
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    to: string
  }
>

export function Link({ to, onClick, children, ...props }: LinkProps) {
  const { navigate } = useRouter()

  const isStatusDomain = window.location.hostname === 'status.blastcore.it'
  const isAbsolute = /^(?:[a-z]+:)?\/\//i.test(to)
  
  let href = to
  if (isStatusDomain && !isAbsolute && to !== '/status' && to !== '/status/') {
    const mainDomain = window.location.hostname.replace('status.', '')
    href = `${window.location.protocol}//${mainDomain}${to}`
  }

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event)
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }

    if (/^(?:[a-z]+:)?\/\//i.test(href)) {
      return
    }

    event.preventDefault()
    navigate(href)
  }

  return (
    <a href={href} onClick={handleClick} {...props}>
      {children}
    </a>
  )
}

type NavLinkProps = Omit<LinkProps, 'className'> & {
  className?: string | ((state: { isActive: boolean }) => string | undefined)
  end?: boolean
}

export function NavLink({
  className,
  end = false,
  to,
  ...props
}: NavLinkProps) {
  const { pathname } = useRouter()
  const isActive =
    pathname === to || (!end && to !== '/' && pathname.startsWith(`${to}/`))
  const resolvedClass =
    typeof className === 'function' ? className({ isActive }) : className

  return <Link className={resolvedClass} to={to} {...props} />
}

export function useLocation() {
  const { pathname, search } = useRouter()
  return { pathname, search }
}

export function useParams(): Record<string, string> {
  const { pathname } = useRouter()
  const match =
    /^\/player\/([^/]+)\/?$/.exec(pathname) ??
    /^\/tournaments\/([^/]+)\/?$/.exec(pathname)
  if (match?.[1] === undefined) return {}

  try {
    const key = pathname.startsWith('/player/') ? 'uuid' : 'id'
    return { [key]: decodeURIComponent(match[1]) }
  } catch {
    return {}
  }
}

type SearchParamsInput = URLSearchParams | Record<string, string>

export function useSearchParams(): [
  URLSearchParams,
  (next: SearchParamsInput) => void,
] {
  const { pathname, search, navigate } = useRouter()
  const params = useMemo(() => new URLSearchParams(search), [search])

  const setParams = useCallback(
    (next: SearchParamsInput) => {
      const resolved =
        next instanceof URLSearchParams ? next : new URLSearchParams(next)
      const query = resolved.toString()
      navigate(query.length > 0 ? `${pathname}?${query}` : pathname)
    },
    [navigate, pathname],
  )

  return [params, setParams]
}
